import { DateTime } from 'luxon';

import {
  type RouterCapacityTestItem,
  type RouterNetworkDay,
  type RouterNetworkEventItem,
  type RouterNetworkSimSummary,
} from '@okvc/schema';

import { formatDurationMs } from '../../MediaStatus/format.js';
import { CARRIER_LABELS } from '../routerSimForm.js';

const MINUTES_PER_HOUR = 60;
const AXIS_PROFILE_PATTERN = /^(?:lq|hq)-(\d+p)-(\d+)fps$/;
const REOLINK_PROFILE_LABELS: Record<string, string> = {
  'reolink-sub': 'Reolink sub stream',
  'reolink-main': 'Reolink main stream',
};

export const OFFLINE_SERIES_KEY = 'offline';
export const UNKNOWN_SERIES_KEY = 'unknown';

export type EventTone = 'success' | 'error' | 'warning' | 'info';

export interface NetworkEventDescription {
  title: string;
  detail: string | null;
  tone: EventTone;
}

export type AvailabilityRow = Record<string, string | number>;

export function simLabel(sim: Pick<RouterNetworkSimSummary, 'carrier' | 'isPrimary'>): string {
  return `${CARRIER_LABELS[sim.carrier]} (${sim.isPrimary ? 'primary' : 'backup'})`;
}

export function describeNetworkEvent(event: RouterNetworkEventItem): NetworkEventDescription {
  const toCarrier = event.toCarrier ? CARRIER_LABELS[event.toCarrier] : 'another SIM';
  const offlineFor = event.downtimeMs ? `Offline for ${formatDurationMs(event.downtimeMs)}` : null;
  switch (event.type) {
    case 'sim_switched': {
      const fromCarrier = event.fromCarrier ? ` from ${CARRIER_LABELS[event.fromCarrier]}` : '';
      return { title: `Switched${fromCarrier} to ${toCarrier}`, detail: offlineFor, tone: 'info' };
    }
    case 'link_down': {
      const carrier = event.fromCarrier ? `Was on ${CARRIER_LABELS[event.fromCarrier]}` : null;
      return { title: 'Router went offline', detail: carrier, tone: 'error' };
    }
    case 'link_up':
      return { title: `Back online on ${toCarrier}`, detail: offlineFor, tone: 'success' };
    case 'flapping':
      return { title: 'Network unstable', detail: 'More than 3 SIM switches in 15 minutes', tone: 'warning' };
    case 'switch_requested':
      return { title: `Switch to ${toCarrier} requested`, detail: null, tone: 'info' };
    case 'switch_failed':
      return { title: `Switch to ${toCarrier} failed`, detail: 'The router stayed on its current SIM', tone: 'error' };
  }
}

export function profileLabel(profile: string | null): string {
  if (!profile) {
    return 'None passed';
  }
  if (REOLINK_PROFILE_LABELS[profile]) {
    return REOLINK_PROFILE_LABELS[profile];
  }
  const match = AXIS_PROFILE_PATTERN.exec(profile);
  return match ? `${match[1]} · ${match[2]} fps` : profile;
}

export function availabilityRows(days: RouterNetworkDay[]): AvailabilityRow[] {
  return days.map((day) => {
    const simMinutes = Object.entries(day.minutesBySim);
    const simHours = simMinutes.map(([simId, minutes]): [string, number] => [simId, toHours(minutes)]);
    return {
      label: DateTime.fromISO(day.day).toFormat('LLL d'),
      ...Object.fromEntries(simHours),
      [OFFLINE_SERIES_KEY]: toHours(day.offlineMinutes),
      [UNKNOWN_SERIES_KEY]: toHours(day.unknownMinutes),
    };
  });
}

export function minutesOnSim(days: RouterNetworkDay[], simId: string): number {
  return days.reduce((total, day) => total + (day.minutesBySim[simId] ?? 0), 0);
}

export function testedStreamLimit(test: RouterCapacityTestItem): number {
  return test.results
    .filter((result) => result.phase === 'ramp')
    .reduce((largest, result) => Math.max(largest, result.concurrentStreams), 1);
}

export function sustainableStreamsLabel(test: RouterCapacityTestItem): string {
  if (test.sustainableHighStreams === null) {
    return '—';
  }
  const reachedTestLimit = test.sustainableHighStreams > 0 &&
    test.sustainableHighStreams === testedStreamLimit(test);
  return reachedTestLimit ? `${test.sustainableHighStreams}+` : String(test.sustainableHighStreams);
}

function toHours(minutes: number): number {
  return Math.round((minutes / MINUTES_PER_HOUR) * 10) / 10;
}
